/**
 * RecommendationsView.jsx - Stock Rebalancing Recommendations Component
 * 
 * Displays transfer recommendations with filters and summary statistics.
 * Extracted from StockRebalancer.jsx for maintainability.
 */
import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Search,
  RefreshCcw,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  X,
} from "lucide-react";

/**
 * RecommendationsView - Displays stock rebalancing recommendations
 * 
 * @param {Object} props
 * @param {Array} props.recommendations - List of recommendation items
 * @param {Object} props.summary - Summary statistics (total_products_analyzed, etc.)
 * @param {Object} props.filteredSummary - Summary for filtered results
 * @param {boolean} props.loading - Loading state
 * @param {number} props.filteredCount - Count of filtered recommendations
 * @param {number} props.totalCount - Total unfiltered count
 * @param {string} props.search - Search query
 * @param {Function} props.onSearchChange - Search change handler
 * @param {string} props.brand - Selected brand filter
 * @param {Function} props.onBrandChange - Brand filter change handler
 * @param {string} props.group - Selected category/group filter
 * @param {Function} props.onGroupChange - Group filter change handler
 * @param {string} props.fromShop - FROM shop filter
 * @param {Function} props.onFromShopChange - FROM shop filter change handler
 * @param {string} props.toShop - TO shop filter
 * @param {Function} props.onToShopChange - TO shop filter change handler
 * @param {Array} props.brandOptions - Available brand options
 * @param {Array} props.groupOptions - Available category/group options
 * @param {Array} props.shopsWithTypes - Shops list with type info
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Function} props.onClearFilters - Clear all filters handler
 * @param {Function} props.onCreateTransfer - CREATE TRANSFER button handler
 */
export default function RecommendationsView({
  recommendations = [],
  summary = null,
  filteredSummary = null,
  loading = false,
  filteredCount = 0,
  totalCount = 0,
  search = "",
  onSearchChange,
  brand = "",
  onBrandChange,
  group = "",
  onGroupChange,
  fromShop = "",
  onFromShopChange,
  toShop = "",
  onToShopChange,
  brandOptions = [],
  groupOptions = [],
  shopsWithTypes = [],
  onRefresh,
  onClearFilters,
  onCreateTransfer,
}) {
  // Check if any filters are active
  const hasFilters = search || brand || group || fromShop || toShop;

  return (
    <div className="space-y-4">
      {/* Filters Section */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by product name, brand..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8 h-9"
              data-testid="rec-search-input"
            />
          </div>
          
          {/* Brand Filter */}
          <Select value={brand || "__all__"} onValueChange={(v) => onBrandChange(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-[160px] h-9" data-testid="rec-brand-filter">
              <SelectValue placeholder="All Brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Brands</SelectItem>
              {brandOptions.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Category Filter */}
          <Select value={group || "__all__"} onValueChange={(v) => onGroupChange(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-[160px] h-9" data-testid="rec-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Categories</SelectItem>
              {groupOptions.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* FROM Shop Filter */}
          <Select 
            value={fromShop || "__all__"} 
            onValueChange={(v) => {
              const newFrom = v === "__all__" ? "" : v;
              onFromShopChange(newFrom);
            }}
          >
            <SelectTrigger className="w-[140px] h-9" data-testid="rec-from-shop-filter">
              <SelectValue placeholder="From: All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">From: All Shops</SelectItem>
              {shopsWithTypes.map((s) => (
                <SelectItem 
                  key={s.name} 
                  value={s.name}
                  disabled={toShop === s.name}
                >
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* TO Shop Filter */}
          <Select 
            value={toShop || "__all__"} 
            onValueChange={(v) => {
              const newTo = v === "__all__" ? "" : v;
              onToShopChange(newTo);
            }}
          >
            <SelectTrigger className="w-[140px] h-9" data-testid="rec-to-shop-filter">
              <SelectValue placeholder="To: All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">To: All Shops</SelectItem>
              {shopsWithTypes.map((s) => (
                <SelectItem 
                  key={s.name} 
                  value={s.name}
                  disabled={fromShop === s.name}
                >
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Clear Filters */}
          {hasFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClearFilters}
              className="h-9 px-3"
              data-testid="rec-clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
          
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRefresh}
            disabled={loading}
            className="h-9 px-3"
            data-testid="rec-refresh-btn"
          >
            <RefreshCcw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Filter count */}
        {!loading && (
          <div className="text-xs text-muted-foreground mt-2" data-testid="rec-filter-count">
            {hasFilters ? (
              <span>Showing <strong>{filteredCount}</strong> of {totalCount} recommendations</span>
            ) : (
              <span>{filteredCount} recommendations</span>
            )}
          </div>
        )}
      </Card>
      
      {/* Summary */}
      {summary && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-xs text-muted-foreground">Products Analyzed</div>
              <div className="text-xl font-semibold">{summary.total_products_analyzed}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Products with Recommendations</div>
              <div className="text-xl font-semibold text-purple-600">
                {hasFilters && filteredSummary ? filteredSummary.products_with_recommendations : summary.products_with_recommendations}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total Transfers Suggested</div>
              <div className="text-xl font-semibold">
                {hasFilters && filteredSummary ? filteredSummary.total_recommended_transfers : summary.total_recommended_transfers}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total Units to Move</div>
              <div className="text-xl font-semibold">
                {hasFilters && filteredSummary ? filteredSummary.total_recommended_units : summary.total_recommended_units}
              </div>
            </div>
            {hasFilters && (
              <div className="text-xs text-muted-foreground self-end">
                (filtered results)
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Recommendations List */}
      {loading ? (
        <Card className="p-8 text-center text-muted-foreground">Loading recommendations...</Card>
      ) : recommendations.length === 0 ? (
        <Card className="p-8 text-center">
          {hasFilters ? (
            <>
              <Search className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <div className="font-medium" data-testid="rec-no-match">No Matching Recommendations</div>
              <div className="text-sm text-muted-foreground mt-1">
                No recommendations match your current filters.
              </div>
              <Button variant="outline" size="sm" className="mt-3" onClick={onClearFilters}>
                Clear Filters
              </Button>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
              <div className="font-medium">No Recommendations</div>
              <div className="text-sm text-muted-foreground mt-1">
                All configured products have balanced stock across shops, or no thresholds are configured yet.
              </div>
            </>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {recommendations.map((rec, idx) => (
            <Card key={idx} className="p-4" data-testid={`recommendation-${idx}`}>
              <div className="flex flex-col gap-3">
                {/* Top Row: Product + Transfer Info */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{rec.product_name}</div>
                    <div className="text-xs text-muted-foreground">{rec.brand_name}</div>
                  </div>

                  {/* Transfer Info */}
                  <div className="flex items-center gap-3 text-sm flex-shrink-0">
                    <div className="text-center">
                      <div className="font-medium text-red-600">{rec.source_shop_name}</div>
                      <div className="text-xs text-muted-foreground">
                        Qty: {rec.source_current_qty}
                        <Badge variant="outline" className="ml-1 text-[8px] bg-slate-100">
                          {rec.source_shop_type === "warehouse" ? "WH" : "NOR"}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-amber-600">
                        Surplus: {rec.source_current_qty - rec.source_effective_max}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center gap-0.5">
                      <ArrowRight className="w-5 h-5 text-blue-500" />
                      <Badge className="font-mono bg-blue-600 text-white">
                        {rec.recommended_qty}
                      </Badge>
                    </div>
                    
                    <div className="text-center">
                      <div className="font-medium text-emerald-600">{rec.destination_shop_name}</div>
                      <div className="text-xs text-muted-foreground">
                        Qty: {rec.destination_current_qty}
                        <Badge variant="outline" className="ml-1 text-[8px] bg-slate-100">
                          {rec.destination_shop_type === "warehouse" ? "WH" : "NOR"}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-red-600">
                        Deficit: {rec.destination_effective_min - rec.destination_current_qty}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Threshold Info + Action Button */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t">
                  {/* Threshold Info */}
                  <div className="text-xs text-muted-foreground flex gap-3">
                    <span>
                      Src max: {rec.source_effective_max}
                      {rec.source_threshold_source === "custom" && (
                        <Badge variant="outline" className="ml-1 text-[8px] bg-indigo-50 text-indigo-600">CUSTOM</Badge>
                      )}
                    </span>
                    <span>
                      Dest min: {rec.destination_effective_min}
                      {rec.destination_threshold_source === "custom" && (
                        <Badge variant="outline" className="ml-1 text-[8px] bg-indigo-50 text-indigo-600">CUSTOM</Badge>
                      )}
                    </span>
                  </div>
                  
                  {/* CREATE TRANSFER Button */}
                  {onCreateTransfer && (
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                      onClick={() => onCreateTransfer(rec)}
                      data-testid={`create-transfer-${idx}`}
                    >
                      <ArrowRight className="w-4 h-4 mr-1" />
                      Create Transfer
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Note about recommendations */}
      <Card className="p-3 bg-amber-50 border-amber-200">
        <div className="flex gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-amber-800">
            <strong>Note:</strong> These are suggestions only. Stock quantities shown are from the time recommendations were generated.
            To execute a transfer, use the Transfer tab and verify current stock before proceeding.
          </div>
        </div>
      </Card>
    </div>
  );
}
